import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const GalleryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80vw;
  margin: 0 auto;
  padding: 1rem;
`;

const MainImage = styled.img`
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  margin-bottom: 2rem;
`;

const ThumbnailsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
  width: 100%;
`;

const Thumbnails = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
`;

const Thumbnail = styled.img`
  flex: 0 0 auto;
  width: 100px;
  height: 100px;
  object-fit: cover;
  cursor: pointer;
  opacity: ${(props) => (props.active ? 1 : 0.5)};
  border: ${(props) => (props.active ? "2px solid black" : "none")};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

export const ImageGallery = ({ imageSources }) => {
  const [currentImage, setCurrentImage] = useState(imageSources?.[0]);
  const thumbnailsRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const currentIndex = imageSources?.indexOf(currentImage);

  const scrollThumbnailToIndex = (index) => {
    if (thumbnailsRef.current) {
      const container = thumbnailsRef.current;
      const thumbnailWidth = 100 + 8;
      container.scrollTo({
        left: index * thumbnailWidth - 50,
        behavior: "smooth",
      });
    }
  };

  const handleThumbnailClick = (src, index) => {
    setCurrentImage(src);
    scrollThumbnailToIndex(index);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const deltaX = touchStartX.current - touchEndX.current;
      const sensitivity = 50;

      if (deltaX > sensitivity && currentIndex < imageSources.length - 1) {
        setCurrentImage(imageSources[currentIndex + 1]);
      } else if (deltaX < -sensitivity && currentIndex > 0) {
        setCurrentImage(imageSources[currentIndex - 1]);
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  useEffect(() => {
    if (imageSources && imageSources.length > 0) {
      handleThumbnailClick(imageSources[0], 0); // <- kliknij pierwszy thumbnail tylko raz
    }
  }, []);

  useEffect(() => {
    if (!imageSources || imageSources.length === 0) return;

    const index = imageSources.indexOf(currentImage);
    if (index !== -1) {
      scrollThumbnailToIndex(index);
    }
  }, [currentImage, imageSources]);

  if (!imageSources || imageSources.length === 0) return null;

  return (
    <GalleryContainer>
      <MainImage
        src={currentImage}
        alt="Main display"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
      <ThumbnailsWrapper>
        <Thumbnails ref={thumbnailsRef}>
          {imageSources.map((src, index) => (
            <Thumbnail
              key={index}
              src={src}
              alt={`Thumbnail ${index}`}
              active={src === currentImage}
              onClick={() => handleThumbnailClick(src, index)}
            />
          ))}
        </Thumbnails>
      </ThumbnailsWrapper>
    </GalleryContainer>
  );
};
